import { SortableItemUIModel } from "@/Common/Models/UI/SortableItemUIModel";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";

interface ISortableItemRenderFunction<T> {
    (input: T, idx: number): JSX.Element
}

interface ISortableItemContainerProps<T> {
    ComponentId: string;
    DragGroupClass: string;
    ItemRenderingFunction?: ISortableItemRenderFunction<T>;
    ItemGroupRenderingFunction?: ISortableItemRenderFunction<T[]>;
    SortableItems: Immutable.List<SortableItemUIModel<T>>;
    OnSortDoneOperation?: number;
    OnSortDoneFunction?: (itemId: string, itemIndex: number, startGroup: string, endGroup: string) => void;
    OnSortDone?: (item: T, itemIndex: number) => void;
    DragStartDelegate?: (event: JQueryEventObject, ui: JQueryUI.SortableUIParams) => void;
    DragStopDelegate?: (event: JQueryEventObject, ui: JQueryUI.SortableUIParams) => void;
    isMedicationModel?: boolean;
}

class SortableItemContainerComponent<T> extends React.Component<ISortableItemContainerProps<T>, never> {
    private sortableReference: JQuery = null;

    public render(): JSX.Element {
        var self = this;
        var sortableItems: JSX.Element[] = [];
        const isGroupRendering = this.props.ItemGroupRenderingFunction != null && this.props.ItemGroupRenderingFunction != undefined;

        sortableItems = this.props.SortableItems.toArray().map((x, idx: number) => {
            // @ts-ignore: Ignore error
            var itemRender: JSX.Element = isGroupRendering ? this.props.ItemGroupRenderingFunction(x.Model, idx) : this.props.ItemRenderingFunction(x.Model, idx);
            return (
                <li id={x.ItemId} key={`_sitem_${x.ItemId}`} onTouchStart={this.CheckInput.bind(this)}>
                    {itemRender}
                </li>
            );
        });

        var Hashinput = self.props.ComponentId;
        sortableItems.forEach(x => {
            Hashinput += x.key;
        });

        // use new-guid as key -- will cause react to just throw away old component and re-render new one.
        // if not done react will throw up when switching items from a group to another group because JQuery sortable modifies the DOM, and
        // items in the real DOM will no longuer correspond to items in react virtual DOM, and react reconciliation will throw errors
        // This has performance impact, but should be manageable as long as we have a relatively small amount of sortable items
        // DV : Fixed by hasing the child items Ids into a new Key. This way, if you have to rerender without changing the childs or their sorting position, you dont rerender the component
        return (
            <div id={this.props.ComponentId + "_container"} className={this.props.DragGroupClass} key={this.hash(Hashinput)}>
                <ul
                    id={this.props.ComponentId}
                    className={`${this.props.DragGroupClass} sortable-dragzone`}
                    data-group={this.props.ComponentId}
                >
                    {sortableItems}
                </ul>
            </div>
        );
    }

    public hash(value: string): string {
        var hash = 0, i, chr;
        if (value.length === 0)
            return hash.toString();

        for (i = 0; i < value.length; i++) {
            chr = value.charCodeAt(i);
            hash = ((hash << 5) - hash) + chr;
            hash |= 0; // Convert to 32bit integer
        }
        return hash.toString();
    }
    // public shouldComponentUpdate(nextProps: ISortableItemContainerProps<T>, nextState) {
    //    return true;
    // }

    public componentDidUpdate(): void {
        this.SetupDragAndSort();
    }

    public componentWillUpdate(): void {
        var self = this;

        if (self.sortableReference !== null) {
            $("#" + self.props.ComponentId).off("sort");
            $("#" + self.props.ComponentId).sortable?.("destroy");
            self.sortableReference = null;
        }
    }

    public componentDidMount(): void {
        this.SetupDragAndSort();
    }

    public componentWillUnmount(): void {
        var self = this;

        if (self.sortableReference !== null) {
            $("#" + self.props.ComponentId).off("sort");
            $("#" + self.props.ComponentId).sortable?.("destroy");
            self.sortableReference = null;
        }
    }

    public CheckInput(touch: React.TouchEvent): void {
        if ($(touch.target).is("input") || $(touch.target).is("textarea")) {
            event.stopPropagation();
            $(touch.target).focus();
        }
        else {
            event.preventDefault();
        }
    }

    public SetupDragAndSort(): void {
        var self = this;

        var IsFirefox = (navigator.userAgent.indexOf("Firefox") != -1);

        if (self.sortableReference !== null) {
            $("#" + self.props.ComponentId).off("sort");
            $("#" + self.props.ComponentId).sortable("destroy");
            self.sortableReference = null;
        }

        self.sortableReference = $("#" + self.props.ComponentId).sortable?.({
            delay: 100, // to avoid interference between click & drag events
            connectWith: "." + self.props.DragGroupClass,
            start: (event, ui) => {
                self.props.DragStartDelegate?.(event, ui);

                var currentIndex = ui.item.index();

                // to check if group/index combo is same in update method
                $(ui.item).attr("data-startindex", currentIndex);

                $(ui.item).removeAttr("data-startgroup");
            },
            stop: (event, ui) => {
                if (self.props.OnSortDone) {
                    const itemId = $(ui.item).attr("id");
                    const itemIndex = ui.item.index();
                    self.props.OnSortDone(self.props.SortableItems.find(p => p.ItemId == itemId).Model, itemIndex);
                    return;
                }
                ui.item.closest(".drag-scope-a").find(".drag-container").removeClass("over");
                self.props.DragStopDelegate?.(event, ui);
            },
            out: (event, ui) => {
                var startGroup = $(ui.item).attr("data-startgroup");
                var currentGroup = $(event.target).attr("data-group");
                var dataReset = $(ui.item).attr("data-reset");

                if (
                    self.props.DragGroupClass?.length > 0 // set start group when moving an item from a multi-group sort
                    && startGroup == undefined            // set start group only when we leave original group
                    && !dataReset                         // do not set startgroup ifwe have a datareset attribute (just after update aka after a drop from a group to another)]
                    && !self.props.isMedicationModel                 // do not set startgroup if we are in the activeMedicalComponent
                ) {
                    // to check if group/index combo is same in update method
                    $(ui.item).attr("data-startgroup", currentGroup);
                }

                // out event called last after update... remove reset attribute
                if (dataReset) {
                    $(ui.item).removeAttr("data-startgroup");
                }
            },
            update: (event, ui) => {
                ui.item.closest(".drag-scope-a").find(".drag-container").removeClass("over");

                const itemId: string = $(ui.item).attr("id");
                const itemIndex: number = ui.item.index();
                const startGroup: string = $(ui.item).attr("data-startgroup");
                const endGroup: string = $(event.target).attr("data-group");

                if (startGroup == endGroup) {
                    // Update gets called once for the group for which an item has been removed, and once for for group that receives the item
                    // if we are here it means Update was called from the sourcre group, and we do not call backend resorting
                    // (we will do it on the secong Update call coming from the destination group)
                }
                else {
                    window.setTimeout(() => {
                        if (self.props.OnSortDoneOperation) {
                            const operation = new LocalEvent(self.props.OnSortDoneOperation);
                            operation.AddItem("itemId", itemId);
                            operation.AddItem("itemIndex", itemIndex);
                            operation.AddItem("startGroup", startGroup);
                            operation.AddItem("destinationGroup", endGroup);
                            LocalEventDispatcher.dispatch(operation);
                        }

                        if (self.props.OnSortDoneFunction) {
                            self.props.OnSortDoneFunction(itemId, itemIndex, startGroup, endGroup);
                        }
                    }, 0);

                    // clean up our helper drag attributes
                    $(ui.item).removeAttr("data-startindex");
                    $(ui.item).removeAttr("data-startgroup");
                    $(ui.item).attr("data-reset", "true");
                }
            }
        });

        // Lol...
        if (!IsFirefox) {
            $("#" + self.props.ComponentId).disableSelection?.();
        }
    }
}

export { ISortableItemContainerProps, ISortableItemRenderFunction, SortableItemContainerComponent };
